import type {
  CrmOrganizationUnitItem,
  CrmOrganizationUnitListFilters,
  CrmOrganizationUnitMutation,
  CrmOrganizationUnitTreeRow,
} from "../types";

/** 归一化组织单元筛选条件。 */
export function normalizeCrmOrganizationUnitFilters(
  filters: CrmOrganizationUnitListFilters,
): CrmOrganizationUnitListFilters {
  return {
    province: normalizeOptionalText(filters.province),
    city: normalizeOptionalText(filters.city),
    schoolKeyword: normalizeOptionalText(filters.schoolKeyword),
    schoolSpellCode: normalizeOptionalText(filters.schoolSpellCode),
    unitKeyword: normalizeOptionalText(filters.unitKeyword),
    unitType: filters.unitType,
    isDisabled: filters.isDisabled,
  };
}

/** 归一化组织单元保存载荷。 */
export function normalizeCrmOrganizationUnitMutation(
  payload: CrmOrganizationUnitMutation,
): CrmOrganizationUnitMutation {
  return {
    orgId: payload.orgId,
    parentUnitId: payload.parentUnitId,
    unitType: payload.unitType,
    unitName: payload.unitName.trim(),
    orgStatus: payload.orgStatus,
    effectiveFrom: normalizeOptionalText(payload.effectiveFrom),
    effectiveTo: normalizeOptionalText(payload.effectiveTo),
    isDisabled: payload.isDisabled,
    remark: normalizeOptionalText(payload.remark),
  };
}

/** 根据当前行占用统计构造删除阻断原因。 */
export function buildCrmOrganizationUnitDeleteBlockedReasons(item: CrmOrganizationUnitItem) {
  const reasons: string[] = [];
  if (item.childCount > 0) {
    reasons.push("存在下级节点");
  }
  if (item.contactCount > 0) {
    reasons.push("存在联系人引用");
  }
  if (item.customerCount > 0) {
    reasons.push("存在客户引用");
  }
  return reasons;
}

/** 将分页组织单元构造成学校-学院-系-专业树形表格数据。 */
export function buildCrmOrganizationUnitTreeTable(
  items: CrmOrganizationUnitItem[],
  filters: CrmOrganizationUnitListFilters,
): { rows: CrmOrganizationUnitTreeRow[]; expandedRowKeys: string[] } {
  const schoolMap = new Map<number, CrmOrganizationUnitTreeRow>();
  const unitMap = new Map<number, CrmOrganizationUnitTreeRow>();
  const pendingChildren = new Map<number, CrmOrganizationUnitTreeRow[]>();

  for (const item of sortItemsForPathAttachment(items)) {
    const schoolRoot = ensureSchoolRoot(schoolMap, item);
    const row = createUnitRow(item);
    unitMap.set(item.id, row);
    const parentUnitId = resolveEffectiveParentUnitId(item);
    if (parentUnitId) {
      const parent = unitMap.get(parentUnitId);
      if (parent) {
        appendChild(parent, row);
      } else {
        const siblings = pendingChildren.get(parentUnitId) ?? [];
        siblings.push(row);
        pendingChildren.set(parentUnitId, siblings);
      }
    } else {
      appendChild(schoolRoot, row);
    }

    for (const child of pendingChildren.get(item.id) ?? []) {
      appendChild(row, child);
    }
    pendingChildren.delete(item.id);
  }

  for (const item of items) {
    const row = unitMap.get(item.id);
    const schoolRoot = schoolMap.get(item.orgId);
    if (!row || !schoolRoot || findRowByKey(schoolRoot.children ?? [], row.key)) {
      continue;
    }
    attachRowByPath(schoolRoot, row, item);
  }

  const rows = Array.from(schoolMap.values()).map(sortTreeRow);
  const expandedRowKeys = resolveExpandedRowKeys(rows, filters);
  return { rows, expandedRowKeys };
}

/** 补挂断开的分页节点时先处理浅层真实节点，避免专业先生成临时系后又出现真实系。 */
function sortItemsForPathAttachment(items: CrmOrganizationUnitItem[]) {
  return [...items].sort((left, right) => {
    const levelDelta = left.levelNo - right.levelNo;
    return levelDelta || left.unitPathName.localeCompare(right.unitPathName, "zh-CN") || left.id - right.id;
  });
}

/** 归一化可选查询文本。 */
function normalizeOptionalText(value?: string) {
  const normalized = value?.trim();
  return normalized ? normalized : undefined;
}

/** 创建学校根节点。 */
function ensureSchoolRoot(
  schoolMap: Map<number, CrmOrganizationUnitTreeRow>,
  item: CrmOrganizationUnitItem,
) {
  const existed = schoolMap.get(item.orgId);
  if (existed) {
    return existed;
  }
  const root: CrmOrganizationUnitTreeRow = {
    key: `school:${item.orgId}`,
    orgId: item.orgId,
    schoolName: item.schoolName,
    schoolSpellCode: item.schoolSpellCode,
    province: item.province,
    city: item.city,
    unitName: item.schoolName,
    unitType: "学校",
    unitPathName: item.schoolName,
    isSchoolRoot: true,
    isSyntheticAncestor: false,
    children: [],
  };
  schoolMap.set(item.orgId, root);
  return root;
}

/** 创建真实组织节点行。 */
function createUnitRow(item: CrmOrganizationUnitItem): CrmOrganizationUnitTreeRow {
  return {
    key: `unit:${item.id}`,
    orgId: item.orgId,
    schoolName: item.schoolName,
    schoolSpellCode: item.schoolSpellCode,
    province: item.province,
    city: item.city,
    unitName: item.unitName,
    unitType: item.unitType,
    unitPathName: item.unitPathName || item.unitName,
    isSchoolRoot: false,
    isSyntheticAncestor: false,
    sourceItem: item,
    contactCount: item.contactCount,
    customerCount: item.customerCount,
    childCount: item.childCount,
    isDisabled: item.isDisabled,
    isDisabledLabel: item.isDisabledLabel,
    updatedAt: item.updatedAt,
    children: [],
  };
}

/** 历史数据可能缺失 parent_unit_id，此时用学院/系锚点兜底恢复展示树。 */
function resolveEffectiveParentUnitId(item: CrmOrganizationUnitItem): number | undefined {
  if (item.parentUnitId) {
    return item.parentUnitId;
  }
  if (item.unitType === "专业") {
    return item.departmentUnitId ?? item.collegeUnitId;
  }
  if (item.unitType === "系") {
    return item.collegeUnitId;
  }
  return undefined;
}

/** 缺少父级行时，按路径补齐展示用祖先节点，避免搜索专业时页面变成孤儿行。 */
function attachRowByPath(
  schoolRoot: CrmOrganizationUnitTreeRow,
  row: CrmOrganizationUnitTreeRow,
  item: CrmOrganizationUnitItem,
) {
  const pathParts = (item.unitPathName || item.unitName)
    .split("/")
    .map((part) => part.trim())
    .filter(Boolean);
  const ancestorParts = pathParts.slice(0, -1);
  let parent = schoolRoot;
  ancestorParts.forEach((part, index) => {
    const key = `synthetic:${item.orgId}:${ancestorParts.slice(0, index + 1).join("/")}`;
    let ancestor = parent.children?.find((child) => child.key === key);
    if (!ancestor) {
      ancestor = {
        key,
        orgId: item.orgId,
        schoolName: item.schoolName,
        schoolSpellCode: item.schoolSpellCode,
        province: item.province,
        city: item.city,
        unitName: part,
        unitType: resolveSyntheticUnitType(index),
        unitPathName: ancestorParts.slice(0, index + 1).join(" / "),
        isSchoolRoot: false,
        isSyntheticAncestor: true,
        children: [],
      };
      ancestor = appendChild(parent, ancestor);
    }
    parent = ancestor;
  });
  appendChild(parent, row);
}

/** 根据路径层级推断补齐祖先节点类型。 */
function resolveSyntheticUnitType(index: number) {
  if (index === 0) {
    return "二级学院";
  }
  if (index === 1) {
    return "系";
  }
  return "组织节点";
}

/** 追加子节点并避免重复。 */
function appendChild(
  parent: CrmOrganizationUnitTreeRow,
  child: CrmOrganizationUnitTreeRow,
): CrmOrganizationUnitTreeRow {
  parent.children ??= [];
  const existedByKey = parent.children.find((item) => item.key === child.key);
  if (existedByKey) {
    mergeTreeRowChildren(existedByKey, child);
    return existedByKey;
  }

  const existedBySemantic = parent.children.find((item) => isSameSemanticNode(item, child));
  if (existedBySemantic) {
    if (existedBySemantic.isSyntheticAncestor && !child.isSyntheticAncestor) {
      mergeTreeRowChildren(child, existedBySemantic);
      parent.children[parent.children.indexOf(existedBySemantic)] = child;
      return child;
    }
    mergeTreeRowChildren(existedBySemantic, child);
    return existedBySemantic;
  }

  parent.children.push(child);
  return child;
}

/** 判断同一父级下两个节点是否表达同一个业务层级，用于合并临时节点和真实节点。 */
function isSameSemanticNode(left: CrmOrganizationUnitTreeRow, right: CrmOrganizationUnitTreeRow) {
  return left.unitName === right.unitName && left.unitType === right.unitType;
}

/** 将源节点的子树迁移到目标节点，迁移过程中继续复用追加逻辑做递归去重。 */
function mergeTreeRowChildren(target: CrmOrganizationUnitTreeRow, source: CrmOrganizationUnitTreeRow) {
  for (const child of source.children ?? []) {
    appendChild(target, child);
  }
}

/** 查找当前树中是否已经存在某节点。 */
function findRowByKey(rows: CrmOrganizationUnitTreeRow[], key: string): CrmOrganizationUnitTreeRow | undefined {
  for (const row of rows) {
    if (row.key === key) {
      return row;
    }
    const matched = findRowByKey(row.children ?? [], key);
    if (matched) {
      return matched;
    }
  }
  return undefined;
}

/** 稳定排序树节点，保持学校、学院、系、专业层级阅读顺序。 */
function sortTreeRow(row: CrmOrganizationUnitTreeRow): CrmOrganizationUnitTreeRow {
  const children = [...(row.children ?? [])]
    .sort((left, right) => {
      const levelDelta = resolveUnitTypeOrder(left.unitType) - resolveUnitTypeOrder(right.unitType);
      return levelDelta || left.unitName.localeCompare(right.unitName, "zh-CN");
    })
    .map(sortTreeRow);
  return { ...row, children: children.length > 0 ? children : undefined };
}

/** 节点类型排序权重。 */
function resolveUnitTypeOrder(unitType: string) {
  if (unitType === "学校") {
    return 0;
  }
  if (unitType === "二级学院") {
    return 1;
  }
  if (unitType === "系") {
    return 2;
  }
  if (unitType === "专业") {
    return 3;
  }
  return 4;
}

/** 根据查询意图计算默认展开行。 */
function resolveExpandedRowKeys(
  rows: CrmOrganizationUnitTreeRow[],
  filters: CrmOrganizationUnitListFilters,
) {
  const hasSchoolQuery = Boolean(
    normalizeOptionalText(filters.schoolKeyword) ||
      normalizeOptionalText(filters.schoolSpellCode) ||
      normalizeOptionalText(filters.province) ||
      normalizeOptionalText(filters.city),
  );
  const hasUnitQuery = Boolean(normalizeOptionalText(filters.unitKeyword) || filters.unitType);
  if (!hasSchoolQuery && !hasUnitQuery) {
    return [];
  }
  const expanded = new Set<string>();
  for (const row of rows) {
    collectExpandableRowKeys(row, expanded);
  }
  return Array.from(expanded);
}

/** 收集所有有下级的节点 key。 */
function collectExpandableRowKeys(row: CrmOrganizationUnitTreeRow, expanded: Set<string>) {
  if (row.children && row.children.length > 0) {
    expanded.add(row.key);
    row.children.forEach((child) => collectExpandableRowKeys(child, expanded));
  }
}
